import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Item, Subsystem, SubsystemsResponseDTO } from "@dto/architecture.dto";
import { CustomLinkItem, CustomLinksFlatResponseDTO, CustomLinksResponseDTO, CustomLinkUpsertDTO, ItemCustomLinksByItem, UpdateCustomLinkDTO } from "@dto/customLinks.dto";
import { Button, Card, Flex, Form, Modal, Popconfirm, TabsProps } from "antd";
import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GridScope } from "src/component/grid/Grid";
import { ColProps } from "src/component/grid/gridTypes";
import { XmHelp } from "src/component/help/XmHelp";
import { XmSelect } from "src/component/select/XmSelect";
import { makeItemLabelRoot } from "src/component/selectItems/children/Items";
import { regexApi } from "src/component/selectItems/comp/preview/Preview";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import XmTabsSticky from "src/component/stickyTab/XmTabsSticky";
import { CLASS_ORDER } from "src/content/configuration/cg/cgTypes";
import { API_URL } from "src/data/Api";
import useDebounce from "src/hook/useDebounce";
import useGetApiData from "src/hook/useGetApiData";
import { getApi, postApi, putApi } from "src/util/apiCalls";
import { GLOB } from "src/util/Glob";
import { Log } from "../../../../service/log.service";
import { getAllSubsystems } from "../../alerting/alertingService";
import { HwTypeSelect, HwTypeSelectProps } from "../../confChanges/CfgFilter/modal/Form";
import CustomLink from "./CustomLink";
import "./CustomLinks.less";
import CustomLinksFormList, { CustomLinkEntry, makeEmptyLink } from "./CustomLinksFormList";

const CustomLinks = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState<{ items: ItemCustomLinksByItem[], subs: Subsystem[]; }>({ items: [], subs: [] });

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([getApi<CustomLinksResponseDTO>(`${API_URL.CONFIGURATION}/custom-links`, controller.signal), getAllSubsystems()]).then(
      ([{ data: { data: items } }, { data: { data: subs } }]) => setApiData({ items, subs }),
      (reason: AxiosError) => Log.error('Failed to get custom links!', reason)
    ).finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  const tabs = useMemo<TabsProps['items']>(() => {
    if (isLoading)
      return undefined;

    const initTabs: TabsProps['items'] = [];

    CLASS_ORDER.forEach((c: string) => {
      const active = GLOB.menuService.activeClasses.find(({ class: cls }) => cls === c);
      if (!active)
        return;

      initTabs.push({
        label: active.title,
        key: c,
        children:
          <CustomLinksTable
            key={c}
            cls={c}
            items={apiData.items.filter(l => l.class === c)}
            subs={apiData.subs}
          />
      });
    });

    return initTabs;
  }, [isLoading, apiData]);

  if (isLoading)
    return <XmSpinner />;

  return <XmTabsSticky items={tabs} />;
};

interface CustomLinksTableProps {
  cls: string;
  items: ItemCustomLinksByItem[];
  subs: Subsystem[];
}

const CustomLinksTable = ({ cls, items, subs }: CustomLinksTableProps) => {
  const [gs] = useState(() => new GridScope<ItemCustomLinksByItem>());
  const [customLinks, setCustomLinks] = useState<ItemCustomLinksByItem[]>(() => items.map(i => toRow(i, subs)).sort(byLabel));
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemCustomLinksByItem | null>(null);

  const openAddModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item: ItemCustomLinksByItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleModalOk = (values: { item: CustomLinkItem; links: ItemCustomLinksByItem['custom_links']; }) => {
    const newItem = toRow({ ...values.item, custom_links: values.links }, subs);

    setCustomLinks(prev => [...prev.filter(i => i.item_id !== values.item.item_id), newItem].sort(byLabel));
    setModalOpen(false);
  };

  // TODO: refac BE to expose a DELETE method; for now an empty PUT clears the item's links
  const removeItem = (item: ItemCustomLinksByItem) => {
    const label = getItemLabel(item);
    putApi<CustomLinksFlatResponseDTO, UpdateCustomLinkDTO[]>(`${API_URL.CONFIGURATION}/custom-links/${item.item_id}`, []).then(
      () => {
        setCustomLinks(prev => prev.filter(i => i.item_id !== item.item_id));
        Log.info(`Custom links for '${label}' deleted`);
      },
      (reason: AxiosError) => Log.error(`Failed to delete custom links for '${label}'`, reason)
    );
  };

  const columns: ColProps<ItemCustomLinksByItem>[] = [
    {
      title: 'Item',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => GLOB.naturalSort(a.label, b.label),
      filter: { field: ['label'] },
      render: (_, { label, url }) => url ? <Link to={GLOB.menuService.getRouteLink(url)}>{label}</Link> : label
    },
    {
      title: 'Root parent',
      dataIndex: ['root_parent', 'label'],
      key: 'root_parent',
      sorter: (a, b) => GLOB.naturalSort(a.root_parent?.label, b.root_parent?.label),
      filter: { field: ['root_parent', 'label'] },
      render: (_, { root_parent, root_parent_url }) => root_parent ? root_parent_url ? <Link to={GLOB.menuService.getRouteLink(root_parent_url)}>{root_parent.label}</Link> : root_parent.label : undefined
    },
    {
      title: 'Subsystem',
      dataIndex: 'subsystem',
      key: 'subsystem',
      sorter: (a, b) => GLOB.naturalSort(a.subsystem, b.subsystem),
      filter: { field: ['subsystem'] },
    },
    {
      title: 'Device class',
      dataIndex: 'hw_type',
      key: 'hw_type',
      filter: { field: ['hw_type'] },
      sorter: (a, b) => GLOB.naturalSort(a.hw_type, b.hw_type),
    },
    {
      title: 'Links',
      key: 'links',
      width: 'auto',
      filter: { field: ['custom_links'] },
      onFilter: (value, record, isCaseSensitive, isRegex) => {
        const term = value as string;
        return record.custom_links.some(({ label }) => {
          if (isRegex)
            return new RegExp(term).test(label);
          if (isCaseSensitive)
            return label.includes(term);
          return label.toLowerCase().includes(term.toLowerCase());
        });
      },
      plainRender: true,
      render: (_: unknown, record: ItemCustomLinksByItem) => (
        <span style={{ display: 'flex', flexDirection: 'row', gap: '0.5em', alignItems: 'flex-start', padding: '0.25em 0' }}>
          {record.custom_links.map(link => <CustomLink key={link.id} {...link} />)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '7.5em',
      render: (_: unknown, record: ItemCustomLinksByItem) => (
        <span style={{ display: 'flex', gap: '0.25em' }}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Popconfirm title="Remove all links of this item?" onConfirm={() => removeItem(record)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const modalKey = editingItem ? `${editingItem.item_id}-${editingItem.custom_links.length}` : 'new';
  return (
    <div style={{ position: "relative", width: "min-content" }}>
      <Flex align="center" justify="space-between">
        <Button
          style={{ display: 'block', margin: '.5em 0' }}
          onClick={openAddModal}
        >Add Custom link</Button>
        <XmHelp href="https://xormon.com/external-links.php" />
      </Flex>
      <gs.Grid
        dataSource={customLinks}
        columns={columns}
        rowKey={(r) => r.item_id}
        pagination={false}
        size="small"

      />
      <CustomLinkModal
        key={modalKey}
        cls={cls}
        open={modalOpen}
        editingItem={editingItem}
        existingItems={customLinks}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
};

interface FormDataCustomLink {
  custom_links: CustomLinkEntry[];
  item: CustomLinkItem;
}

const getItemLabel = (item: Pick<CustomLinkItem, 'label' | 'root_parent'>) =>
  item.root_parent?.label ? `${item.label} (${item.root_parent.label})` : item.label;

const byLabel = (a: ItemCustomLinksByItem, b: ItemCustomLinksByItem) => GLOB.naturalSort(a.label, b.label);

// label lookups fall back to the raw id when the code isn't in the catalog, instead of crashing on `.label` of undefined
const hwTypeLabel = (hwType: string) =>
  GLOB.menuService.hwTypes.find(ht => ht.hw_type === hwType)?.label ?? hwType;
const subsystemLabel = (subs: Subsystem[], subsystem: string, hwType?: string) =>
  subs.find(s => s.subsystem === subsystem && (hwType === undefined || s.hw_type === hwType))?.label ?? subsystem;

const toRow = (item: ItemCustomLinksByItem, subs: Subsystem[]): ItemCustomLinksByItem => ({
  ...item,
  subsystem: subsystemLabel(subs, item.subsystem, item.hw_type),
  hw_type: hwTypeLabel(item.hw_type),
});

const CustomLinkModal = (
  { cls, open, editingItem, existingItems, onOk, onCancel }: {
    cls: string,
    open: boolean;
    editingItem: ItemCustomLinksByItem | null;
    existingItems: ItemCustomLinksByItem[];
    onOk: (values: { item: CustomLinkItem; links: ItemCustomLinksByItem['custom_links']; }) => void;
    onCancel: () => void;
  }
) => {
  const [form] = Form.useForm<FormDataCustomLink>();
  const { setFieldValue, getFieldValue } = form;
  const hwTypeValue = Form.useWatch(['item', 'hw_type'], form);
  const subsystemValue = Form.useWatch(['item', 'subsystem'], form);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText);
  const isEditing = !!editingItem;
  const [items, setItems] = useState<Item[]>(isEditing ? [editingItem] : []);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  useEffect(() => {
    if (!subsystemValue || isEditing)
      return;

    const controller = new AbortController();
    setIsLoadingSearch(true);

    regexApi(
      { class: cls, hw_type: hwTypeValue, subsystem: subsystemValue, regex: ['.*'] },
      { signal: controller.signal },
      true
    ).then(
      ({ data: { data } }) => setItems(
        data.sort((a, b) => GLOB.naturalSort(a.root_parent ? `${a.root_parent.label} ${a.label}` : a.label, b.root_parent ? `${b.root_parent.label} ${b.label}` : b.label))
      ),
      (reason: AxiosError) => Log.error('Failed to get items!', reason)
    ).finally(() => setIsLoadingSearch(false));

    return () => controller.abort();
  }, [cls, hwTypeValue, subsystemValue]);

  const itemSearchText = (item: Item) =>
    item.root_parent ? `${item.root_parent.label} ${item.label}` : item.label;

  if (!open)
    return <></>;

  const handleFinish = (_: FormDataCustomLink) => {
    const { custom_links: links, item }: FormDataCustomLink = form.getFieldsValue(true);
    const itemLabel = getItemLabel(item);
    const formData = links.map((link): UpdateCustomLinkDTO => ({ ...link, newTab: true, id: '' }));

    const isUpdate = isEditing || existingItems.some(i => i.item_id === item.item_id);
    const url = `${API_URL.CONFIGURATION}/custom-links/${item.item_id}`;
    const request = isUpdate
      ? putApi<CustomLinksFlatResponseDTO, UpdateCustomLinkDTO[]>(url, formData)
      : postApi<CustomLinksFlatResponseDTO, CustomLinkUpsertDTO[]>(url, formData);

    request.then(
      ({ data: { data } }) => {
        onOk({ item: data[0].item, links: data });
        Log.info(`${isUpdate ? 'Updated' : 'Created'} custom links for '${itemLabel}'`);
        form.resetFields();
      },
      (reason: AxiosError) => Log.error(`Failed to ${isUpdate ? 'update' : 'create'} custom links for '${itemLabel}'`, reason)
    );
  };

  const handleChange = ({ hwType = true, subsystem = true }: Partial<Record<'hwType' | 'subsystem', boolean>>) => () => {
    const item = getFieldValue('item');
    setFieldValue('item', {
      ...item,
      hw_type: hwType ? undefined : item.hw_type,
      subsystem: subsystem ? undefined : item.subsystem,
      item_id: undefined,
    });

    setFieldValue('custom_links', [makeEmptyLink()]);
  };

  const search = debouncedSearch.toLowerCase();
  const searchItems = items
    .filter(item => !search || itemSearchText(item).toLowerCase().includes(search))
    .map(item => ({ label: makeItemLabelRoot(item), value: item.item_id, item }));

  let initialValues: { item: Record<string, unknown>; custom_links?: CustomLinkEntry[]; } = {
    item: {
      item_id: undefined,
      label: undefined,
      subsystem: undefined,
      class: cls,
      hw_type: undefined,
      root_parent: undefined,
    }
  };

  if (isEditing) {
    const { custom_links: itemLinks, ...item } = editingItem;
    initialValues = {
      ...initialValues,
      item,
      custom_links: itemLinks.map(({ label, url, color, newTab }) => ({ label, url, color, newTab })),
    };
  }

  return (
    <Modal
      title={editingItem ? 'Edit Custom link' : 'Add Custom link'}
      open={open}
      onOk={() => form.submit()}
      onCancel={() => { form.resetFields(); onCancel(); }}
      destroyOnHidden
    >
      <Form<FormDataCustomLink> className="xm-custom-link-form"
        onFinish={handleFinish}
        form={form}
        layout="horizontal"
        labelCol={{ flex: '7em' }}
        wrapperCol={{ flex: 'auto' }}
        labelAlign="right"
        style={{ display: 'flex', flexDirection: 'column', gap: '1em', marginBottom: '1em' }}
        initialValues={initialValues}
      >
        <Card >
          <Form.Item
            label='HW type'
            name={['item', 'hw_type']}
            required
            rules={[{ required: true, message: 'Please select HW type!' }]}
          >
            <HwTypeSelect
              cls={isEditing ? undefined : cls}
              disabled={isEditing}
              onChange={handleChange({ hwType: false })}
              apiParams={{ active: true, class: cls }}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev: FormDataCustomLink, current) => prev.item.hw_type !== current.item.hw_type}
          >
            {() => (
              <Form.Item
                label='Subsystem'
                name={['item', 'subsystem']}
                rules={[{ required: true, message: 'Please select Subsystem!' }]}
              >
                <SubsystemSelect
                  cls={cls}
                  hwType={getFieldValue(['item', 'hw_type'])}
                  disabled={isEditing || !getFieldValue(['item', 'hw_type'])}
                  onChange={handleChange({ hwType: false, subsystem: false })}
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            label='Item'
            name={['item', 'item_id']}
            rules={[{ required: true, message: 'Please select an item!' }]}
            style={{ marginBottom: 0 }}
          >
            <XmSelect
              placeholder='Search item'
              showSearch
              filterOption={false}
              onSearch={setSearchText}
              loading={isLoadingSearch}
              disabled={isEditing || !subsystemValue}
              options={searchItems}
              onChange={(_value, option) => {
                const selected = (option as { item?: Item; })?.item;
                setFieldValue('item', { ...getFieldValue('item'), ...(selected ?? { item_id: undefined, label: undefined, root_parent: undefined }) });
                const existing = selected && existingItems.find(i => i.item_id === selected.item_id);
                setFieldValue('custom_links', existing
                  ? existing.custom_links.map(({ label, url, color, newTab }) => ({ label, url, color, newTab }))
                  : [makeEmptyLink()]);
              }}
              allowClear
              ignoreReadonly
            />
          </Form.Item>
        </Card>
        <Card >
          <CustomLinksFormList />
        </Card >
      </Form>
    </Modal>
  );
};

type SubsystemSelectProps = HwTypeSelectProps & {
  hwType: string,
};

const SubsystemSelect = ({ cls, hwType, apiParams: _, ...rest }: SubsystemSelectProps) => {
  const [subsystemOptions, , isLoading] = useGetApiData<
    Subsystem[],
    SubsystemsResponseDTO
  >(
    `${API_URL.ARCHITECTURE}/hw_types/${hwType}/subsystems`,
    `Failed to get subsystem list.`,
    [hwType, rest.disabled],
    undefined,
    !hwType || rest.disabled ? 'NO' : 'YES',
    undefined,
    { class: cls, active: true, type: 'customLinks' }
  );

  return (
    <XmSelect
      {...rest}
      options={subsystemOptions}
      loading={isLoading}
      placeholder='Select Subsystem'
      allowClear
      showSearch
      optionFilterProp='label'
      fieldNames={{ value: 'subsystem' }}
      ignoreReadonly
    />
  );
};

export default CustomLinks;